// @ts-nocheck
/**
 * Remove Credits dialog — subtract voice/TTS credits from the target user's
 * balance, capped at the currently-available amount. Logic preserved from v1
 * (regex-clamped decimals, blur-time and inline-time validation, +1 stepper).
 */
import { useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const cx = (...args) => args.filter(Boolean).join(' ');

function RenderRemoveCreditModal(props) {
  const [errorMessage, setErrorMessage] = useState('');

  const handleOpenChange = (next) => {
    if (!next) {
      if (props.submitting) return;
      props.setShow(false);
      props.setRemoveCreditsValue(0);
      setErrorMessage('');
    }
  };

  const availableCredits = Number(props.availableCredits) || 0;
  const displayCredits =
    availableCredits % 1 === 0 ? String(availableCredits) : availableCredits.toFixed(2);

  const handleCreditChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      props.setRemoveCreditsValue(value);
      setErrorMessage('');
      return;
    }

    const regex = /^\d+(\.\d{0,2})?$/;
    if (!regex.test(value)) return;

    const numValue = parseFloat(value);
    if (numValue > availableCredits) {
      setErrorMessage(`Cannot remove more than available credits (${displayCredits})`);
    } else {
      setErrorMessage('');
    }
    props.setRemoveCreditsValue(value);
  };

  const handleBlur = (e) => {
    const value = parseFloat(e.target.value) || 0;
    if (value > availableCredits) {
      setErrorMessage(`Cannot remove more than available credits (${displayCredits})`);
    } else if (value <= 0 && e.target.value !== '') {
      setErrorMessage('Please enter a value greater than 0');
    } else {
      setErrorMessage('');
    }
  };

  const handleStep = () => {
    const currentValue = parseFloat(props.removeCreditsValue) || 0;
    const newValue = currentValue + 1;
    if (newValue > availableCredits) {
      setErrorMessage(`Cannot remove more than available credits (${displayCredits})`);
      props.setRemoveCreditsValue(displayCredits);
    } else {
      setErrorMessage('');
      props.setRemoveCreditsValue(newValue.toString());
    }
  };

  const handleConfirm = () => {
    if (props.submitting) return;
    const value = parseFloat(props.removeCreditsValue) || 0;
    if (value > availableCredits) {
      setErrorMessage(`Cannot remove more than available credits (${displayCredits})`);
      return;
    }
    if (value <= 0) {
      setErrorMessage('Please enter a value greater than 0');
      return;
    }
    setErrorMessage('');
    props.setConfirmReduceUpdateCredit(true);
  };

  const disabled =
    props.submitting ||
    !!errorMessage ||
    !props.removeCreditsValue ||
    parseFloat(props.removeCreditsValue) <= 0;

  return (
    <Dialog open={!!props.show} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{props?.title || 'Remove Voice Credits'}</DialogTitle>
        </DialogHeader>

        <div className="rounded-md border border-sky-200 bg-sky-50 p-3 dark:border-sky-900/40 dark:bg-sky-900/20">
          <div className="text-sm font-medium text-sky-700 dark:text-sky-300">
            {props.availableCreditsLabel || 'Maximum Removable Credits:'}
          </div>
          <div className="text-xl font-semibold text-sky-900 dark:text-sky-100">{displayCredits}</div>
        </div>

        <div className="space-y-2">
          <label htmlFor="removeCreditsValue" className="text-sm font-medium">
            Amount to Remove:
          </label>
          <div className="flex">
            <Input
              id="removeCreditsValue"
              type="number"
              value={props.removeCreditsValue}
              onChange={handleCreditChange}
              onBlur={handleBlur}
              placeholder="0"
              min="0"
              max={availableCredits}
              step="0.01"
              className={cx(
                'rounded-r-none focus-visible:ring-primary',
                errorMessage && 'border-destructive focus-visible:ring-destructive',
              )}
            />
            <Button
              type="button"
              onClick={handleStep}
              className="rounded-l-none px-3"
              aria-label="Increase by 1"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {errorMessage && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
              {errorMessage}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={props.submitting}
          >
            Close
          </Button>
          <Button onClick={handleConfirm} disabled={disabled}>
            {props.submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {props.submitting ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RenderRemoveCreditModal;
